import React, { useState, useRef } from 'react';
import api from '../services/api';
const LINEA_VACIA = { MEDICAMENTO: '', PRESENTACION: 'TAB', CANTIDAD: '', MANANA: '', MEDIODIA: '', NOCHE: '', DIAS: '', INDICACIONES: '' };
// Misma fórmula que usan NEUROMED/Culqui/OncoCare: para tabletas/cápsulas,
// cantidad = (mañana + mediodía + noche) × días; para jarabe/suspensión,
// se dispensa por frascos (mínimo 3). Siempre editable a mano después.
const calcularCantidadFinal = l => {
  if (l.PRESENTACION === 'JBE') {
    const frascos = parseFloat(l.CANTIDAD) || 0;
    return String(Math.max(3, frascos || 3));
  }
  const total = ((parseFloat(l.MANANA)||0)+(parseFloat(l.MEDIODIA)||0)+(parseFloat(l.NOCHE)||0))*(parseFloat(l.DIAS)||0);
  if (total <= 0) return l.CANTIDAD || '';
  return String(total);
};
/**
 * Modal de nueva/editar receta médica — reutilizable desde cualquier
 * pantalla (Odontograma, Recetas, Historia clínica, etc.) sin navegar
 * a otra ruta. Se controla 100% con props: el padre decide cuándo
 * abrirlo y qué hacer cuando se guarda o se cierra.
 *
 * props:
 *  - receta: null para nueva, o el objeto { COD, COD_PACIENTE, DIAGNOSTICO_CIE,
 *            DIAGNOSTICO_DESC, OBSERVACIONES, lineas } para editar
 *  - codPacienteInicial / cieInicial / descInicial: precarga para nueva receta
 *  - onGuardado(): callback cuando se guarda con éxito
 *  - onCerrar(): callback para cerrar el modal sin guardar
 */
export default function RecetaModal({
  receta = null,
  codPacienteInicial = '', cieInicial = '', descInicial = '',
  onGuardado, onCerrar,
}) {
  const [form, setForm] = useState(() => receta ? {
    COD: receta.COD,
    COD_PACIENTE: receta.COD_PACIENTE,
    DIAGNOSTICO_CIE: receta.DIAGNOSTICO_CIE || '',
    DIAGNOSTICO_DESC: receta.DIAGNOSTICO_DESC || '',
    OBSERVACIONES: receta.OBSERVACIONES || '',
    lineas: receta.lineas?.length ? receta.lineas.map(l => ({ PRESENTACION: 'TAB', ...l })) : [{ ...LINEA_VACIA }],
  } : {
    COD: null,
    COD_PACIENTE: codPacienteInicial,
    DIAGNOSTICO_CIE: cieInicial,
    DIAGNOSTICO_DESC: descInicial,
    OBSERVACIONES: '',
    lineas: [{ ...LINEA_VACIA }],
  });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  // Búsqueda CIE-10 en vivo (mismo patrón que Historia.jsx)
  const [cie10Q, setCie10Q] = useState(form.DIAGNOSTICO_CIE);
  const [cie10S, setCie10S] = useState([]);
  const cie10Timer = useRef(null);
  const onCie10 = v => {
    setCie10Q(v);
    setForm(f => ({ ...f, DIAGNOSTICO_CIE: v }));
    clearTimeout(cie10Timer.current);
    if (!v || v.trim().length < 2) { setCie10S([]); return; }
    cie10Timer.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/historia/buscar/cie10?q=${encodeURIComponent(v)}`);
        setCie10S(data);
      } catch (_) { /* búsqueda opcional, no bloquea el formulario */ }
    }, 300);
  };
  const seleccionarCie10 = d => {
    const cod = d.COD_CIE10 || d.COD;
    setForm(f => ({ ...f, DIAGNOSTICO_CIE: cod, DIAGNOSTICO_DESC: d.DIAGNOSTICO }));
    setCie10Q(cod);
    setCie10S([]);
  };
  const cambiarLinea = (i, campo, valor) => {
    setForm(f => {
      const lineas = [...f.lineas];
      lineas[i] = { ...lineas[i], [campo]: valor };
      return { ...f, lineas };
    });
  };
  // Cambia un campo de posología (mañana/mediodía/noche/días) Y recalcula
  // la cantidad total en el mismo paso — igual que NEUROMED.
  const cambiarPosologia = (i, campo, valor) => {
    setForm(f => {
      const lineas = [...f.lineas];
      const nl = { ...lineas[i], [campo]: valor };
      nl.CANTIDAD = calcularCantidadFinal(nl);
      lineas[i] = nl;
      return { ...f, lineas };
    });
  };
  const cambiarPresentacion = (i, presentacion) => {
    setForm(f => {
      const lineas = [...f.lineas];
      lineas[i] = { ...lineas[i], PRESENTACION: presentacion,
        CANTIDAD: presentacion === 'JBE' ? (lineas[i].CANTIDAD || '3') : lineas[i].CANTIDAD };
      return { ...f, lineas };
    });
  };
  const agregarLinea = () => setForm(f => ({ ...f, lineas: [...f.lineas, { ...LINEA_VACIA }] }));
  const quitarLinea = i => setForm(f => ({ ...f, lineas: f.lineas.filter((_, idx) => idx !== i) }));
  const guardar = async () => {
    if (!form.COD_PACIENTE) { setMsg('✕ Falta indicar el paciente'); return; }
    const lineasValidas = form.lineas.filter(l => l.MEDICAMENTO.trim());
    if (lineasValidas.length === 0) { setMsg('✕ Agrega al menos un medicamento'); return; }
    setSaving(true); setMsg('');
    const numOrNull = v => (v === '' || v === null || v === undefined) ? null : Number(v);
    const body = {
      COD_PACIENTE: form.COD_PACIENTE,
      DIAGNOSTICO_CIE: form.DIAGNOSTICO_CIE || null,
      DIAGNOSTICO_DESC: form.DIAGNOSTICO_DESC || null,
      OBSERVACIONES: form.OBSERVACIONES || null,
      lineas: lineasValidas.map(l => ({
        ...l,
        MANANA: numOrNull(l.MANANA),
        MEDIODIA: numOrNull(l.MEDIODIA),
        NOCHE: numOrNull(l.NOCHE),
        DIAS: numOrNull(l.DIAS),
      })),
    };
    try {
      let codReceta = form.COD;
      if (form.COD) {
        await api.put(`/recetas/${form.COD}`, body);
      } else {
        const { data } = await api.post('/recetas', body);
        codReceta = data.COD;
      }
      onGuardado?.(codReceta);
    } catch (e) {
      setMsg('✕ ' + (e.response?.data?.error || 'Error al guardar la receta'));
      setSaving(false);
    }
  };
  return (
    <div className="rm-modal-overlay" onClick={onCerrar}>
      <style>{css}</style>
      <div className="rm-modal" onClick={e => e.stopPropagation()}>
        <div className="rm-modal-hdr">
          <span>{form.COD ? 'Editar receta' : 'Nueva receta'}</span>
          <button className="btn-mo-sec" style={{ padding: '4px 10px' }} onClick={onCerrar}>✕</button>
        </div>
        <div className="rm-modal-body">
          {msg && <div className="rm-msg err">{msg}</div>}
          {!form.COD && !codPacienteInicial && (
            <>
              <div className="mo-sec">Código de paciente *</div>
              <input className="form-control" placeholder="Código del paciente"
                value={form.COD_PACIENTE}
                onChange={e => setForm(f => ({ ...f, COD_PACIENTE: e.target.value }))} />
            </>
          )}
          <div className="rm-form-row">
            <div style={{ position: 'relative' }}>
              <div className="mo-sec">Código CIE-10</div>
              <input className="form-control" placeholder="Busca por nombre o código..."
                value={cie10Q}
                onChange={e => onCie10(e.target.value)} />
              {cie10S.length > 0 && (
                <div className="cie-box">
                  {cie10S.map(d => (
                    <div key={d.COD} className="cie-it" onMouseDown={() => seleccionarCie10(d)}>
                      <strong style={{ color: 'var(--navy)' }}>{d.COD_CIE10 || d.COD}</strong> — {d.DIAGNOSTICO}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <div className="mo-sec">Diagnóstico</div>
              <input className="form-control" placeholder="Ej: Pulpitis irreversible"
                value={form.DIAGNOSTICO_DESC}
                onChange={e => setForm(f => ({ ...f, DIAGNOSTICO_DESC: e.target.value }))} />
            </div>
          </div>
          <div className="mo-sec" style={{ marginTop: 12 }}>Medicamentos</div>
          {form.lineas.map((l, i) => (
            <div key={i} className="rm-linea">
              <div className="rm-linea-top">
                <input className="form-control" placeholder="Medicamento (ej: Ibuprofeno 400mg)"
                  value={l.MEDICAMENTO} onChange={e => cambiarLinea(i, 'MEDICAMENTO', e.target.value)} />
                {form.lineas.length > 1 && (
                  <button className="rm-linea-del" onClick={() => quitarLinea(i)} title="Quitar">
                    <i className="ti ti-x" aria-hidden="true"></i>
                  </button>
                )}
              </div>
              <div className="tipo-btn-row">
                <button type="button" className={`tipo-btn${(l.PRESENTACION||'TAB')==='TAB'?' act':''}`}
                  onClick={() => cambiarPresentacion(i, 'TAB')}>Tabletas/Cápsulas</button>
                <button type="button" className={`tipo-btn${l.PRESENTACION==='JBE'?' act':''}`}
                  onClick={() => cambiarPresentacion(i, 'JBE')}>Jarabe/Suspensión</button>
              </div>
              {l.PRESENTACION === 'JBE' ? (
                <div className="rm-linea-grid5">
                  <div className="rm-linea-posologia">
                    <label>Dosis (mL)</label>
                    <input className="form-control" type="number" min="0" step="0.5" placeholder="0"
                      value={l.MANANA} onChange={e => cambiarLinea(i, 'MANANA', e.target.value)} />
                  </div>
                  <div className="rm-linea-posologia">
                    <label>Veces/día</label>
                    <input className="form-control" type="number" min="0" step="1" placeholder="0"
                      value={l.MEDIODIA} onChange={e => cambiarLinea(i, 'MEDIODIA', e.target.value)} />
                  </div>
                  <div className="rm-linea-posologia">
                    <label>Días</label>
                    <input className="form-control" type="number" min="0" step="1" placeholder="0"
                      value={l.DIAS} onChange={e => cambiarLinea(i, 'DIAS', e.target.value)} />
                  </div>
                  <div className="rm-linea-posologia" style={{ gridColumn: 'span 2' }}>
                    <label>Frascos a dispensar (mín. 3)</label>
                    <input className="form-control" type="number" min="3" step="1" placeholder="3"
                      value={l.CANTIDAD} onChange={e => cambiarLinea(i, 'CANTIDAD', e.target.value)}
                      onBlur={() => cambiarLinea(i, 'CANTIDAD', calcularCantidadFinal(l))} />
                  </div>
                </div>
              ) : (
                <div className="rm-linea-grid5">
                  <div className="rm-linea-posologia">
                    <label>Mañana</label>
                    <input className="form-control" type="number" min="0" step="0.5" placeholder="0"
                      value={l.MANANA} onChange={e => cambiarPosologia(i, 'MANANA', e.target.value)} />
                  </div>
                  <div className="rm-linea-posologia">
                    <label>Medio día</label>
                    <input className="form-control" type="number" min="0" step="0.5" placeholder="0"
                      value={l.MEDIODIA} onChange={e => cambiarPosologia(i, 'MEDIODIA', e.target.value)} />
                  </div>
                  <div className="rm-linea-posologia">
                    <label>Noche</label>
                    <input className="form-control" type="number" min="0" step="0.5" placeholder="0"
                      value={l.NOCHE} onChange={e => cambiarPosologia(i, 'NOCHE', e.target.value)} />
                  </div>
                  <div className="rm-linea-posologia">
                    <label>Días</label>
                    <input className="form-control" type="number" min="0" step="1" placeholder="0"
                      value={l.DIAS} onChange={e => cambiarPosologia(i, 'DIAS', e.target.value)} />
                  </div>
                  <div className="rm-linea-posologia" title="(mañana + mediodía + noche) × días — editable">
                    <label># Total a dispensar</label>
                    <input className="form-control" type="number" min="0" step="1"
                      value={l.CANTIDAD} onChange={e => cambiarLinea(i, 'CANTIDAD', e.target.value)} />
                  </div>
                </div>
              )}
              <input className="form-control" placeholder="Indicación (ej: Una tableta 8am y 8pm)"
                value={l.INDICACIONES} onChange={e => cambiarLinea(i, 'INDICACIONES', e.target.value)} />
            </div>
          ))}
          <button className="od-form-link" onClick={agregarLinea}>+ Agregar otro medicamento</button>
          <div className="mo-sec" style={{ marginTop: 12 }}>Observaciones</div>
          <textarea className="form-control" rows={2}
            value={form.OBSERVACIONES} onChange={e => setForm(f => ({ ...f, OBSERVACIONES: e.target.value }))} />
          <button className="btn-mo-pri" style={{ width: '100%', marginTop: 14 }} disabled={saving} onClick={guardar}>
            {saving ? 'Guardando...' : form.COD ? 'Guardar cambios' : 'Crear receta'}
          </button>
        </div>
      </div>
    </div>
  );
}
const css = `
.rm-msg { padding:8px 14px; border-radius:8px; font-size:12px; margin-bottom:10px; }
.rm-msg.err { background:var(--red-lt); color:var(--red); border:1px solid var(--red); }
.rm-modal-overlay { position:fixed; inset:0; background:rgba(20,25,35,.45); display:flex; align-items:center; justify-content:center; z-index:200; padding:16px; }
.rm-modal { background:var(--surface); border-radius:14px; width:100%; max-width:520px; max-height:88vh; overflow-y:auto; }
.rm-modal-hdr { padding:14px 18px; border-bottom:1px solid var(--border); display:flex; align-items:center; justify-content:space-between; font-size:14px; font-weight:700; color:var(--text); position:sticky; top:0; background:var(--surface); }
.rm-modal-body { padding:16px 18px; }
.rm-form-row { display:grid; grid-template-columns:1fr 1.4fr; gap:8px; }
.rm-linea { border:1px solid var(--border); border-radius:9px; padding:10px; margin-bottom:8px; display:flex; flex-direction:column; gap:6px; background:var(--bg); }
.rm-linea-top { display:flex; gap:6px; align-items:center; }
.rm-linea-top .form-control { flex:1; }
.rm-linea-del { background:none; border:none; color:var(--text3); cursor:pointer; padding:4px; flex-shrink:0; }
.rm-linea-del:hover { color:var(--red); }
.rm-linea-grid5 { display:grid; grid-template-columns:repeat(5,1fr); gap:6px; }
.rm-linea-posologia { display:flex; flex-direction:column; gap:3px; }
.rm-linea-posologia label { font-size:9.5px; color:var(--text3); text-align:center; }
.tipo-btn-row { display:flex; gap:6px; }
.tipo-btn     { padding:4px 14px; border-radius:6px; font-size:10px; cursor:pointer; border:0.5px solid var(--border); color:var(--text3); background:var(--surface2); transition:all .12s; }
.tipo-btn.act { background:var(--navy); color:#fff; border-color:var(--navy); font-weight:600; }
.cie-box { border:1px solid var(--border); border-radius:8px; overflow:hidden; background:var(--surface); max-height:180px; overflow-y:auto; margin-top:4px; position:absolute; z-index:10; width:100%; box-shadow:0 4px 12px rgba(0,0,0,.15); }
.cie-it  { padding:8px 12px; cursor:pointer; font-size:12px; border-bottom:1px solid var(--bg); }
.cie-it:hover { background:var(--blue-lt); }
`;
